import autobind from 'autobind-decorator';
import { duration, reactive, showErrorMsg } from '#imports';
import { message, notification } from 'ant-design-vue';
import { Inject, Injectable, forwardRef } from '@kaokei/use-vue-service';
import armsRum from '@arms/rum-browser';
import { debounce } from 'lodash-es';
import {
  getMsgHistory,
  postMsgReadStatus,
  postMsgSend,
  uploadImFile,
} from '@core/api/im';
import type {
  AudioMsgBodyVO,
  AudioMsgVO,
  CmdMsgBodyVO,
  CmdMsgVO,
  CodeMsgBodyVO,
  CodeMsgVO,
  ImgMsgBodyVO,
  ImgMsgVO,
  MsgBodyVO,
  MsgVO,
  TxtMsgBodyVO,
  TxtMsgVO,
  VideoMsgBodyVO,
  VideoMsgVO,
} from '@core/types/im';
import { uuid } from '@core/utils/uuid';
import {
  IM_APPID,
  IM_AUDIO_COMMUNICATE_NOTIFICATION,
  IM_BUSINESS,
  NEW_MSG_TYPE,
  READ_STATUS,
} from './constant';
import { ImService } from './im.service';
import {
  getImageSize,
  isAudioMsg,
  isCmdRecall,
  isCmdSessionInfo,
  isCmdViewCommunicate,
  isCmdViewCommunicateLose,
  isRenderMsg,
  isTxtMsg,
  isVideoMsg,
} from './utils';

@Injectable()
export class MessageService {
  public current = 1;
  public pageSize = 20;
  public loading = false;
  public loadError = false;
  public hasNext = true;
  public readMsgIdList = [] as string[];
  public newMessageType = NEW_MSG_TYPE.PREPEND;

  @Inject(forwardRef(() => ImService))
  public imService!: ImService;

  public init() {
    const batchReadMsg = this.batchReadMsg.bind(this);
    // 延迟批量标记消息已读
    this.batchReadMsg = debounce(batchReadMsg, 1000);
    // 加载第一页的历史消息
    this.loadMsgHistory();
  }

  // 标记单个消息已读
  // 只处理视觉上已读，不处理音频已读且已播放
  public readMsg(msg: MsgVO, readStatus = READ_STATUS.ONLY_READ) {
    if (msg.readStatus > 0) {
      // 如果已经标记为已读则直接返回
      return;
    }
    // 标记为已读
    msg.readStatus = readStatus;
    if (!msg.preRead) {
      // 需要过滤掉已经提前置为已读的场景
      // 因为此场景unreadCount并没有提前增加1，所以此时也不需要减去1
      this.imService.decreaseUnread();
    }
    if (isRenderMsg(msg)) {
      this.readMsgIdList.push(msg.msgId);
      // 批量标记为已读
      this.batchReadMsg();
    }
  }

  public batchReadMsg(readAll = false) {
    if (this.readMsgIdList.length) {
      const data = {
        user: this.imService.selfId,
        // 设置targetUser则会标记所有消息已读
        targetUser: readAll ? this.imService.peerId : null,
        msgIds: [...this.readMsgIdList],
        readStatus: 1,
      };
      // 清空已读消息ID列表
      this.readMsgIdList = [];
      return postMsgReadStatus(data);
    }
  }

  public readAllMsg() {
    const data = {
      user: this.imService.selfId,
      // 设置targetUser则会标记所有消息已读
      targetUser: this.imService.peerId,
      msgIds: [],
      readStatus: 1,
    };
    // 清空已读消息ID列表
    this.readMsgIdList = [];
    this.imService.clearUnreadCount();
    return postMsgReadStatus(data);
  }

  // 加载历史消息
  // 每次加载成功后，current+1
  public loadMsgHistory() {
    if (this.loading) return;
    this.loading = true;
    this.loadError = false;

    const model = {
      current: this.current,
      pageSize: this.pageSize,
      user: this.imService.selfId,
      targetUsers: [this.imService.peerId],
      recall: 1,
    };

    return getMsgHistory(model)
      .then((res) => {
        if (res) {
          this.hasNext = res.hasNext;
          if (res.hasNext) {
            this.current++;
          }
          if (res.msgList) {
            this.prependMessage(res.msgList);
          }
        }
      })
      .catch((err) => {
        this.loadError = true;
        console.error('[IM] 历史消息加载失败', err);
        armsRum.sendCustom({
          type: 'custom',
          name: 'oj_im_load_history_error',
          value: 1,
        });
      })
      .finally(() => {
        this.loading = false;
      });
  }

  public handleReceiveMessage(msg: MsgVO) {
    if (msg.msgBody.contentType === 'CMD') {
      // 命令消息不加入消息列表
      if (isCmdRecall(msg)) {
        // 收到撤回消息的命令消息，发送撤回成功的命令消息
        const msgId = msg.msgBody.cmdMsg.msgId as string;
        this.recallLocalMessage(msgId);
        const replyMsg = this.createRecallMsg(msgId);
        this.sendMsg(replyMsg);
      } else if (isCmdSessionInfo(msg)) {
        // 收到会话心跳的命令消息，发送会话心跳的命令消息
        const replyMsg = this.createSessionInfoMsg(msg.msgId);
        this.sendMsg(replyMsg);
      } else if (isCmdViewCommunicate(msg)) {
        // 接收到老师端发起语音连麦的请求
        notification.info({
          key: IM_AUDIO_COMMUNICATE_NOTIFICATION,
          message: '语音连麦',
          description: '老师正在请求语音连麦，请前往客户端接听',
          duration: 0,
        });
      } else if (isCmdViewCommunicateLose(msg)) {
        // 接收到关闭语音连麦的请求，需要关闭通知弹窗
        notification.close(IM_AUDIO_COMMUNICATE_NOTIFICATION);
      }
    } else if (isRenderMsg(msg)) {
      this.appendMessage(msg, NEW_MSG_TYPE.APPEND_PEER);
    }
  }

  private recallLocalMessage(msgId: string) {
    const found = this.imService.messages.find((item) => item.msgId === msgId);
    if (found) {
      // 置为已撤回
      found.msgBody.recall = 1;
      if (found.readStatus === 0) {
        // 当前消息未读则置为已读，并且减少未读消息数量
        found.readStatus = 1;
        this.imService.decreaseUnread();
      }
    }
  }

  public appendMessage(msg: MsgVO | MsgVO[], type = NEW_MSG_TYPE.APPEND_SELF) {
    this.newMessageType = type;
    const decorated = this.decorateMessage(msg);
    this.imService.messages.push(...decorated);
  }

  public prependMessage(msg: MsgVO | MsgVO[]) {
    this.newMessageType = NEW_MSG_TYPE.PREPEND;
    const decorated = this.decorateMessage(msg);
    this.imService.messages.unshift(...decorated);
  }

  // 预先生成辅助字段，以避免在渲染模板时进行复杂的逻辑判断
  private decorateMessage(msg: MsgVO | MsgVO[]) {
    const lastMsg = this.imService.messages[this.imService.messages.length - 1];
    const msgList = Array.isArray(msg) ? [lastMsg, ...msg] : [lastMsg, msg];
    const res = [] as MsgVO[];
    // 忽略第一个消息，因为它只是用来和第二个消息比较时间的
    for (let i = 1; i < msgList.length; i++) {
      const prev = msgList[i - 1];
      const msg = msgList[i];
      const isMine = msg.sender === this.imService.selfId;
      msg.isMine = isMine;
      msg.avatar = isMine
        ? this.imService.self.avatar
        : this.imService.peer.avatar;
      msg.placement = isMine ? 'end' : 'start';
      msg.showDatetime = this.isMsgNew(prev, msg);
      res.push(msg);
    }
    return res;
  }

  private isMsgNew(prev: MsgVO, next: MsgVO) {
    if (!prev) return true;
    // 时间间隔超过5分钟，认为是新消息
    return next.msgTime - prev.msgTime > 5 * 60;
  }

  // 通用发送消息方法
  // 适合自定义发送图片、音频、视频等需要上传文件的场景
  private sendMsg(msg: MsgVO) {
    msg.loading = true;
    msg.error = false;
    return postMsgSend(msg)
      .then((res) => {
        if (res?.SendResult?.sensitive) {
          // 设置当前消息为敏感消息，实际上是发送失败了，并且不允许重新发送
          msg.sensitive = true;
          this.imService.messageApi.error(
            '您发送的信息涉及违规，请修改后再发送',
          );
          armsRum.sendCustom({
            type: 'custom',
            name: 'oj_im_send_msg_sensitive',
            value: 1,
          });
        } else {
          armsRum.sendCustom({
            type: 'custom',
            name: 'oj_im_send_msg_success',
            value: 1,
          });
        }
      })
      .catch(() => {
        msg.error = true;
        armsRum.sendCustom({
          type: 'custom',
          name: 'oj_im_send_msg_error',
          value: 1,
        });
        armsRum.sendException({
          name: 'ImSendMsgError',
          message: '' + msg.msgBody.messageId,
        });
      })
      .finally(() => {
        msg.loading = false;
      });
  }

  // 通用发送并且添加到消息列表的方法
  // 这个方法适合可以直接同步发送消息的场景
  // 比如文本消息和命令消息，不适合图片、音频、视频等需要上传文件的场景
  private addAndSendMsg(msg: MsgVO) {
    this.appendMessage(msg);
    return this.sendMsg(msg);
  }

  private updateMsgTime(msg: MsgVO) {
    msg.msgTime = Date.now() / 1000;
    return msg;
  }
  private addBizCode(msg: MsgVO) {
    if (isTxtMsg(msg)) {
      msg.bizCode = 'OnlineClass-IM-U3D';
    }
    return msg;
  }

  // 重新发送消息
  // 适合重新发送文本、命令等不需要上传文件的场景
  public resendMsg(msg: MsgVO) {
    this.updateMsgTime(msg);
    this.sendMsg(msg);
  }

  // 发送文本消息
  public sendTxtMsg(txt: string) {
    const msg = this.createTxtMsg(txt);
    this.addAndSendMsg(msg);
  }

  // 发送图片消息
  public sendImgMsg(url: string) {
    const msg = this.createImgMsg(url);
    this.addAndSendMsg(msg);
  }
  // 上传图片文件并且发送图片消息
  public async sendImgMsgByFile(file: File) {
    const previewUrl = URL.createObjectURL(file);
    const msg = this.createImgMsg(previewUrl);
    const size = await getImageSize(file);
    if (size) {
      // 预先设置好图片的宽高，避免图片加载完成前消息气泡大小不对
      // 这里的宽高是图片的实际宽高，不是展示时的宽高
      // 展示时会根据最大宽高进行等比缩放
      msg.msgBody.imgMsg.width = size.width;
      msg.msgBody.imgMsg.height = size.height;
    }
    msg.file = file;
    this.appendMessage(msg);
    this.uploadAndSendImg(msg);
  }
  // 重新发送图片消息
  public resendImgMsg(msg: ImgMsgVO) {
    this.updateMsgTime(msg);
    this.uploadAndSendImg(msg);
  }

  // 发送音频消息
  public sendAudioMsg(url: string, audioLen: number) {
    const msg = this.createAudioMsg(url, audioLen);
    this.addAndSendMsg(msg);
  }
  // 上传音频文件并且发送音频消息
  public sendAudioMsgByFile(file: File, duration: number) {
    const previewUrl = URL.createObjectURL(file);
    const msg = this.createAudioMsg(previewUrl, duration);
    msg.file = file;
    this.appendMessage(msg);
    this.uploadAndSendAudio(msg);
  }
  // 重新发送图片消息
  public resendAudioMsg(msg: AudioMsgVO) {
    this.updateMsgTime(msg);
    this.uploadAndSendAudio(msg);
  }

  // 发送视频消息
  public sendVideoMsg(url: string, backgroundImage: string) {
    const msg = this.createVideoMsg(url, backgroundImage);
    this.addAndSendMsg(msg);
  }

  // 发送命令消息
  public sendSessionInfoMsg(replyMessageId: string) {
    const msg = this.createSessionInfoMsg(replyMessageId);
    this.addAndSendMsg(msg);
  }

  // 发送代码消息
  public sendCodeMsg(logo: string, title: string, url: string) {
    const msg = this.createCodeMsg(logo, title, url);
    this.addAndSendMsg(msg);
  }

  // 通用的包装消息body返回完整消息体
  private createMsg(msgBody: MsgBodyVO, defaults: Partial<MsgVO> = {}) {
    msgBody.extra = msgBody.extra || {};
    // 标记是来自核桃OJ的消息
    msgBody.extra.htoj = 1;
    const msgId = uuid();
    const msg = {
      msgBody,
      msgId: msgId,
      msgTime: 0,
      readStatus: 0, // 历史逻辑，消息未读
      // receiver: 'ms:30205135',
      receivers: defaults.receivers || [this.imService.peerId],
      sender: this.imService.selfId,
      sensitive: false,
      senderState: 0,
      business: IM_BUSINESS,
      appId: IM_APPID,
      uuid: msgId,
    } as MsgVO;
    // Txt消息需要增加bizCode参数
    this.addBizCode(msg);
    // 统一设置为当前时间戳
    this.updateMsgTime(msg);
    // 这里一定需要转化为响应式对象，否则在消息列表中修改loading、error等字段不会触发视图更新
    return reactive(msg);
  }

  // 创建文本消息
  private createTxtMsg(txt: string) {
    const msgBody = this.createTxtMsgBody(txt);
    return this.createMsg(msgBody) as TxtMsgVO;
  }
  private createTxtMsgBody(txt: string): TxtMsgBodyVO {
    return {
      contentType: 'TXT',
      txtMsg: { txt },
      extra: {},
      messageId: Date.now(),
      entranceType: 'Normal',
    };
  }

  // 创建图片消息
  private createImgMsg(url: string) {
    const msgBody = this.createImgMsgBody(url);
    return this.createMsg(msgBody) as ImgMsgVO;
  }
  private createImgMsgBody(url: string): ImgMsgBodyVO {
    return {
      contentType: 'IMG',
      imgMsg: { url },
      extra: {},
      messageId: Date.now(),
      entranceType: 'Normal',
    };
  }

  // 创建音频消息
  private createAudioMsg(url: string, audioLen: number) {
    const msgBody = this.createAudioMsgBody(url, audioLen);
    return this.createMsg(msgBody) as AudioMsgVO;
  }
  private createAudioMsgBody(url: string, audioLen: number): AudioMsgBodyVO {
    return {
      contentType: 'AUDIO',
      audioMsg: { url },
      extra: {
        audioLen: `${audioLen}`,
      },
      messageId: Date.now(),
      entranceType: 'Normal',
    };
  }

  // 创建视频消息
  private createVideoMsg(url: string, backgroundImage: string) {
    const msgBody = this.createVideoMsgBody(url, backgroundImage);
    return this.createMsg(msgBody) as VideoMsgVO;
  }
  private createVideoMsgBody(
    url: string,
    backgroundImage: string,
  ): VideoMsgBodyVO {
    return {
      contentType: 'VIDEO',
      videoMsg: { url, backgroundImage },
      extra: {},
      messageId: Date.now(),
      entranceType: 'Normal',
    };
  }

  // 创建session_info命令消息
  private createSessionInfoMsg(replyMessageId: string) {
    const msgBody = this.createSessionInfoMsgBody(replyMessageId);
    return this.createMsg(msgBody, { receivers: ['system'] }) as CmdMsgVO;
  }
  private createSessionInfoMsgBody(replyMessageId: string): CmdMsgBodyVO {
    return {
      contentType: 'CMD',
      cmdMsg: {
        type: 'session_info',
        statusCode: 1,
        replyMessageId,
      },
      extra: {},
      messageId: Date.now(),
      entranceType: 'Normal',
    };
  }

  // 创建recall命令消息
  private createRecallMsg(msgId: string) {
    const msgBody = this.createRecallMsgBody(msgId);
    return this.createMsg(msgBody) as CmdMsgVO;
  }
  private createRecallMsgBody(msgId: string): CmdMsgBodyVO {
    return {
      contentType: 'CMD',
      cmdMsg: {
        type: 'recall',
        status: 1,
        msgId,
      },
      extra: {},
      messageId: Date.now(),
      entranceType: 'Normal',
    };
  }

  // 创建代码消息
  private createCodeMsg(logo: string, title: string, url: string) {
    const msgBody = this.createCodeMsgBody(logo, title, url);
    return this.createMsg(msgBody) as CodeMsgVO;
  }
  private createCodeMsgBody(
    logo: string,
    title: string,
    url: string,
  ): CodeMsgBodyVO {
    return {
      contentType: 'CODE',
      codeMsg: { logo, title, url, needLogin: 1 },
      extra: {},
      messageId: Date.now(),
      entranceType: 'Normal',
    };
  }

  // 上传图片文件并且发送图片消息
  private uploadAndSendImg(msg: ImgMsgVO) {
    msg.loading = true;
    msg.error = false;
    const blobUrl = msg.msgBody.imgMsg.url;
    const isBlob = blobUrl.startsWith('blob:');
    return Promise.resolve(blobUrl)
      .then((url) => {
        if (isBlob) {
          return uploadImFile(msg.file as File);
        } else {
          return { url };
        }
      })
      .then(({ url }) => {
        if (isBlob) {
          URL.revokeObjectURL(blobUrl);
          delete msg.file;
        }
        msg.msgBody.imgMsg.url = url;
        return this.sendMsg(msg);
      })
      .catch(() => {
        msg.error = true;
      })
      .finally(() => {
        msg.loading = false;
      });
  }

  // 上传音频文件并且发送音频消息
  private uploadAndSendAudio(msg: AudioMsgVO) {
    msg.loading = true;
    msg.error = false;
    const blobUrl = msg.msgBody.audioMsg.url;
    const isBlob = blobUrl.startsWith('blob:');
    return Promise.resolve(blobUrl)
      .then((url) => {
        if (isBlob) {
          return uploadImFile(msg.file as File);
        } else {
          return { url };
        }
      })
      .then(({ url }) => {
        if (isBlob) {
          URL.revokeObjectURL(blobUrl);
          delete msg.file;
        }
        msg.msgBody.audioMsg.url = url;
        return this.sendMsg(msg);
      })
      .catch(() => {
        msg.error = true;
      })
      .finally(() => {
        msg.loading = false;
      });
  }
}
